import { useMemo, useRef } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Tag, TST_COLUMN_SIZE, TSTTable } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import * as theme from "./Table.css";
import { AssignedAs, ControlView } from "./useUserGroupsListTable";

interface UserGroupControlsSubTableProps {
    controls: ControlView[];
}

export function UserGroupControlsSubTable({
    controls,
}: UserGroupControlsSubTableProps) {
    const { t } = useTranslation();
    const tableRef = useRef<HTMLDivElement>(null);

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<ControlView>();
        return [
            columnHelper.accessor((control) => control.name, {
                id: "name",
                header: () => t("AccessAndAssignment_Controls_Control"),
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <ProtectedLink
                            href={row.controlUrl}
                            hasAccess={row.canView}
                        >
                            {info.getValue()}
                        </ProtectedLink>
                    );
                },
                size: TST_COLUMN_SIZE.XL,
            }),
            columnHelper.accessor((control) => control.programName, {
                id: "controlProgram",
                header: () => t("ControlProgram"),
                size: TST_COLUMN_SIZE.L,
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <ProtectedLink
                            href={row.programUrl}
                            hasAccess={row.canViewProgram}
                        >
                            {info.getValue()}
                        </ProtectedLink>
                    );
                },
            }),
            columnHelper.accessor(
                (control) =>
                    assignedAsToLocalizedStrings(t, control.assignedAs).join(
                        ", "
                    ),
                {
                    id: "assignedAs",
                    header: () => t("AccessAndAssignment_Controls_AssignedAs"),
                    size: TST_COLUMN_SIZE.L,
                    cell: (info) => (
                        <AssignedAsCell
                            assignedAs={assignedAsToLocalizedStrings(
                                t,
                                info.row.original.assignedAs
                            )}
                        />
                    ),
                }
            ),
        ];
    }, [t]);

    const subTable = useReactTable({
        data: controls,
        columns,
        initialState: {
            sorting: [{ id: "name", desc: false }],
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <TSTTable
            tableRef={tableRef}
            table={subTable}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

interface AssignedAsProps {
    assignedAs: string[];
}
function AssignedAsCell({ assignedAs }: AssignedAsProps) {
    return (
        <span className={theme.assignedCellWrapper}>
            {assignedAs.map((role) => (
                <Tag key={role}>{role}</Tag>
            ))}
        </span>
    );
}

function assignedAsToLocalizedStrings(
    t: TFunction,
    assignedAs: AssignedAs
): string[] {
    const roles: string[] = [];
    if (assignedAs.isAssignee) {
        roles.push(t("UserManagement_AssignedAsResponsible"));
    }
    if (assignedAs.isReviewer) {
        roles.push(t("UserManagement_AssignedAsReviewer"));
    }
    if (assignedAs.isSecondReviewer) {
        roles.push(t("UserManagement_AssignedAsSecondReviewer"));
    }
    return roles;
}
